import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/state/store';
import { setUploadBatch } from '@/state/translationFlowSlice';
import { useActiveWorkflow } from './useActiveWorkflow';

/**
 * Routes this bootstrap may redirect away from: the translation flow's own screens,
 * plus the bare root, where landing on the wrong one is a mistake worth correcting.
 *
 * Everywhere else the user asked to be is left alone. That is the whole point of the
 * set: the HITL-ready email links to /translated-files/:batchId (COPA-808), and a
 * recipient who still had a workflow open was bounced straight off it to /analysis —
 * the one screen they were not trying to reach, having clicked a link that said their
 * file was ready.
 * The same held for /admin.
 */
const REDIRECTABLE_ROUTES = new Set(['/', '/upload', '/analysis', '/translate']);

/**
 * One-time reconciliation on app load. When /me first reports an active workflow,
 * sets the batch id and (if the user landed on a flow screen) redirects to the stage
 * screen. Fires at most once per app load so it never fights in-session forward
 * navigation; ongoing protection of /upload is handled by RequireNoActiveWorkflow.
 */
export function ActiveWorkflowBootstrap() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeWorkflow, stageRoute, isLoading, isFetching, isUninitialized } = useActiveWorkflow();
  const done = useRef(false);

  useEffect(() => {
    // Reconcile exactly once, and only after /me has SETTLED (resolved to data or
    // error). The pre-auth window is the trap: while auth isn't ready /me is
    // SKIPPED, and RTK reports isLoading=false/isFetching=false for a skipped
    // query — so we must also gate on isUninitialized. Without it the one-shot
    // latches on the very first commit (before /me ever runs, in OIDC prod) and
    // then never funnels a deep-linked user to their active workflow. Ongoing
    // protection of /upload is RequireNoActiveWorkflow's job, not this bootstrap.
    if (done.current || isUninitialized || isLoading || isFetching) return;
    done.current = true;
    if (activeWorkflow && stageRoute) {
      // Unconditional: the flow slice must reflect the active batch wherever the
      // user landed, so a later move onto a flow screen cold-starts correctly.
      dispatch(setUploadBatch(activeWorkflow.batchUploadId));
      if (REDIRECTABLE_ROUTES.has(location.pathname) && location.pathname !== stageRoute) {
        navigate(stageRoute, { replace: true });
      }
    }
  }, [activeWorkflow, stageRoute, isUninitialized, isLoading, isFetching, dispatch, navigate, location.pathname]);

  return null;
}
